import axios from "axios";
import {
  DocumentSnapshot,
  Transaction as FirestoreTransaction,
  Timestamp,
  collection,
  doc,
  documentId,
  getCountFromServer,
  getDoc,
  getDocs,
  increment,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  where,
  arrayUnion,
  updateDoc,
} from "firebase/firestore";
import { v4 } from "uuid";
import {
  MiningInfos,
  SignInData,
  SignUpData,
  User,
  UserModelData,
} from "../types";
import { TransactionData } from "../WebAppsSharedStuffs/types/types";
import {
  ADS_DAILY_ATH_CAP,
  BOX_PRICES,
  FIRST_DEPOSIT_MIN_PI,
  FLASH_BOOST_COST,
  FORGE_PRICES,
  FREE_BOX_ADS_REQUIRED,
  GAMES_DAILY_ATH_CAP,
  GRANT_AD_BOOSTS,
  INSTANT_REFILL_COST,
  MAX_GRANT_MULTIPLIER,
  SKIPS_PER_DAY,
  SPIN_COOLDOWN_MS,
  SPIN_DAILY_ATH_CAP,
  SPIN_REWARDS,
  SpinReward,
  STAKE_BOOST_MAX_EXTRA,
  TURBO_ADS_REQUIRED,
  TURBO_DEPOSIT_MIN_PI,
} from "../WebAppsSharedStuffs/utils/rewardTables";
import { db } from "../WebAppsSharedStuffs/utils/firebase";
import {
  LOCAL_STORAGE_DATA,
  LocalStorageController,
} from "../WebAppsSharedStuffs/utils/LocalStorageController";
import {
  arthereumTokenomics,
  countRecentMiners,
  createCombinedId,
  encrypt,
  getNextGrantDate,
  validatePiNetworkAccessToken,
} from "./arthereum";
import { PiNetworkService } from "./PiIntegration";

export { db };

const todayKey = () => new Date().toISOString().split("T")[0];
const isProData = (d: any) =>
  !!d?.isLifetimePro ||
  (!!d?.isPro && d?.proExpiry?.toDate && d.proExpiry.toDate() > new Date());

class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message);
    this.name = "APIError";
  }
}

class ArthereumProvider {
  // Cache for expensive queries
  private leaderboardCache: { data: User[]; timestamp: number } | null = null;
  private CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private async storeToken(token: string) {
    LocalStorageController.connect().storeData(
      LOCAL_STORAGE_DATA.TOKEN_KEY,
      token
    );
    this.setAuthToken(token);
  }

  async getUserByUid(uid: string): Promise<User> {
    const userRef = doc(db, "athUsers", uid);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new APIError("User not found", 404);
    }
    return userDoc.data() as User;
  }

  async getUserModelDataByUid(uid: string): Promise<UserModelData> {
    const user = await this.getUserByUid(uid);
    const miningReferredUserCount = await countRecentMiners(uid);
    const miningInfos = {
      baseGrant: arthereumTokenomics.calculateBaseGrant(),
      nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
        miningReferredUserCount,
        user.stakeBoost
      ),
      miningReferredUserCount: miningReferredUserCount,
      nextGrantDate: user.lastMiningDate
        ? getNextGrantDate((user.lastMiningDate as any).toDate())
        : new Date(),
    };

    return { user, miningInfos };
  }

  async getStoredToken(): Promise<string | null> {
    return LocalStorageController.connect().getData(
      LOCAL_STORAGE_DATA.TOKEN_KEY
    );
  }

  async removeToken() {
    await LocalStorageController.connect().deleteData(
      LOCAL_STORAGE_DATA.TOKEN_KEY
    );
    this.setAuthToken(null);
  }

  private async updateStreak(userDoc: any): Promise<User> {
    const userData = userDoc.data() as User;
    const now = new Date();
    const lastLogin = userData.lastLoginDate
      ? new Date(userData.lastLoginDate.seconds * 1000)
      : null;

    let newStreak = userData.currentStreak || 0;
    const todayStr = now.toISOString().split("T")[0];
    const lastLoginStr = lastLogin ? lastLogin.toISOString().split("T")[0] : "";

    if (lastLoginStr !== todayStr) {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split("T")[0];

      if (lastLoginStr === yesterdayStr) {
        newStreak += 1;
      } else {
        newStreak = 1;
      }

      await runTransaction(db, async (transaction) => {
        transaction.update(userDoc.ref, {
          currentStreak: newStreak,
          lastLoginDate: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });

      userData.currentStreak = newStreak;
      userData.lastLoginDate = {
        seconds: Math.floor(now.getTime() / 1000),
      } as any;
    }

    return userData;
  }

  async signIn(signInData: SignInData): Promise<UserModelData> {
    try {
      const { email, password, path, piAccessToken } = signInData;

      // Check if we're on localhost with mock data
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1" ||
        window.location.hostname.includes("athtest");

      if (isLocalhost && path === "pi" && piAccessToken === "xxx") {
        // Mock data for localhost development
        const piData = { username: "RichAdams" };

        const usersCollection = collection(db, "athUsers");
        const userQuery = query(
          usersCollection,
          where("piUsername", "==", piData.username),
          limit(1)
        );

        const userSnapshot = await getDocs(userQuery);
        if (userSnapshot.empty) {
          throw new APIError("Invalid credentials", 401);
        }
 
        const userDoc = userSnapshot.docs[0];
        const userData = await this.updateStreak(userDoc);

        const lastMiningDate = userData.lastMiningDate
          ? (userData.lastMiningDate as Timestamp).toDate()
          : null;

        const miningReferredUserCount = await countRecentMiners(userData.id);
        const miningInfos = {
          baseGrant: arthereumTokenomics.calculateBaseGrant(),
          nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
            miningReferredUserCount,
            userData.stakeBoost
          ),
          miningReferredUserCount: miningReferredUserCount,
          nextGrantDate: lastMiningDate
            ? getNextGrantDate(lastMiningDate)
            : new Date(),
        };

        return {
          user: userData,
          miningInfos,
        };
      }

      // Original logic for non-localhost or non-mock scenarios
      let userQuery;
      const usersCollection = collection(db, "athUsers");
      if (path === "pi" && piAccessToken) {
        const piData = await validatePiNetworkAccessToken(piAccessToken);
        userQuery = query(
          usersCollection,
          where("piUsername", "==", piData.username),
          limit(1)
        );
      } else if (email && password) {
        const encryptedPassword = encrypt(password);
        userQuery = query(
          usersCollection,
          where("email", "==", email.trim().toLowerCase()),
          where("password", "==", encryptedPassword),
          limit(1)
        );
      } else {
        throw new Error("Invalid sign-in data provided.");
      }

      const userSnapshot = await getDocs(userQuery);
      if (userSnapshot.empty) {
        throw new APIError("Invalid credentials", 401);
      }
 
      const userDoc = userSnapshot.docs[0];
      const userRef = userDoc.ref;
      let userData = await this.updateStreak(userDoc);

      // Automated Pro expiration check on login
      if (userData.isPro && userData.proExpiry) {
        const expiry = (userData.proExpiry as Timestamp).toDate();
        if (expiry < new Date()) {
          console.log("Pro subscription expired. Updating status...");
          await runTransaction(db, async (transaction) => {
            transaction.update(userRef, {
              isPro: false,
              updatedAt: serverTimestamp(),
            });
          });
          userData.isPro = false;
        }
      }

      const lastMiningDate = userData.lastMiningDate
        ? (userData.lastMiningDate as Timestamp).toDate()
        : null;

      const miningReferredUserCount = await countRecentMiners(userData.id);
      const miningInfos = {
        baseGrant: arthereumTokenomics.calculateBaseGrant(),
        nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
          miningReferredUserCount,
          userData.stakeBoost
        ),
        miningReferredUserCount: miningReferredUserCount,
        nextGrantDate: lastMiningDate
          ? getNextGrantDate(lastMiningDate)
          : new Date(),
      };

      const userModelData: UserModelData = {
        user: userData,
        miningInfos,
      };

      return userModelData; 
    } catch (error) {
      // Re-throw APIError as-is to preserve the error message and status code
      if (error instanceof APIError) {
        throw error;
      }

      // Only wrap non-APIError errors
      throw new APIError("An unexpected error occurred during sign in");
    }
  }

  async signUp(signUpData: SignUpData): Promise<UserModelData> {
    const {
      email,
      password,
      username,
      profileImage,
      invitationCode,
      path,
      piAccessToken,
    } = signUpData;

    try {
      let piData: { username: string; piUid?: string } | null = null;
      let finalUsername = username?.trim();

      // --- 1. Initial Input Validation ---
      if (path === "pi") {
        if (!piAccessToken)
          throw new APIError("Pi Access Token is required.", 400);
        piData = await validatePiNetworkAccessToken(piAccessToken);
        finalUsername = piData.username;
      } else {
        if (!email || !password)
          throw new APIError("Email and password are required.", 400);
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim().toLowerCase())) {
          throw new APIError("Invalid email format.", 400);
        }
        if (password.length < 6) {
          throw new APIError("Password must be at least 6 characters.", 400);
        }
      }

      const usersCollection = collection(db, "athUsers");
      const lowercasedUsername = finalUsername?.toLowerCase();

      // --- 2. Pre-Transaction Checks (Reads) ---
      // Note: This introduces a small chance of a race condition where two users
      // sign up with the same username at the exact same time. The transaction
      // below mitigates some risk, but for 100% uniqueness guarantees, a Cloud Function is best.

      if (path === "pi" && piData) {
        const piUserQuery = query(
          usersCollection,
          where("piUsername", "==", piData.username),
          limit(1)
        );
        const piUserSnapshot = await getDocs(piUserQuery);
        if (!piUserSnapshot.empty) {
          throw new Error("This Pi Network user already has an account.");
        }
      } else if (email) {
        const emailQuery = query(
          usersCollection,
          where("email", "==", email.trim().toLowerCase()),
          limit(1)
        );
        const emailSnapshot = await getDocs(emailQuery);
        if (!emailSnapshot.empty) throw new Error("Email already in use.");
      }

      const usernameQuery = query(
        usersCollection,
        where("lowercasedUsername", "==", lowercasedUsername),
        limit(1)
      );
      const usernameSnapshot = await getDocs(usernameQuery);
      if (!usernameSnapshot.empty) throw new Error("Username already taken.");

      // Check for invitation code and get the referrer's document reference
      let referrerDoc: DocumentSnapshot<any> | null = null;
      if (invitationCode) {
        const referrerQuery = query(
          usersCollection,
          where("lowercasedUsername", "==", invitationCode.trim().toLowerCase()),
          limit(1)
        );
        const referrerSnapshot = await getDocs(referrerQuery);
        if (!referrerSnapshot.empty) {
          referrerDoc = referrerSnapshot.docs[0] as DocumentSnapshot<any>;
        }
      }

      // --- 3. Firestore Transaction (Writes) ---
      const newUserId = v4();
      const result = await runTransaction(
        db,
        async (transaction: FirestoreTransaction) => {
          const referrerId = referrerDoc ? referrerDoc.id : null;

          // Handle referrer updates if a valid one was found
          if (referrerDoc && referrerId) {
            const referrerData = referrerDoc.data();
            const newReferralsCount = (referrerData.referalsCount || 0) + 1;
            const updateData: any = {
              picoinBalance: increment(5), // NEW: 5 Pi bounty for inviting friends!
              arthereumBalance: increment(200), // NEW: 200 ATH Bounty
              referalsCount: newReferralsCount,
            };

            // Milestone Checks
            if (newReferralsCount >= 50) {
              updateData.isLifetimePro = true;
            } else if (newReferralsCount >= 10) {
              // 1 week Pro
              const expiry = new Date();
              expiry.setDate(expiry.getDate() + 7);
              updateData.isPro = true;
              updateData.proExpiry = Timestamp.fromDate(expiry);
            } else if (newReferralsCount >= 3) {
              // 3 days Pro
              const expiry = new Date();
              expiry.setDate(expiry.getDate() + 3);
              updateData.isPro = true;
              updateData.proExpiry = Timestamp.fromDate(expiry);
            }

            transaction.update(referrerDoc.ref, updateData);

            const t1Id = v4();
            const transactionBase = {
              amount: 200,
              crypto: "arthereum",
              type: "referral_bounty",
              combinedUsersId: createCombinedId(newUserId, referrerId),
              usersInvolvedIds: [newUserId, referrerId],
              revoked: false,
              date: serverTimestamp(),
            };
            // Transaction record removed to reduce Firestore write costs
          }

          // Create the new user object
          const newUser = {
            id: newUserId,
            email: path === "pi" ? null : email!.trim().toLowerCase(),
            username: finalUsername,
            lowercasedUsername: lowercasedUsername,
            password: path === "pi" ? null : encrypt(password!),
            profileImage: profileImage || null,
            arthereumBalance: referrerDoc ? 100 : 0, // NEW: 100 ATH Welcome Bonus
            picoinBalance: 0,
            piUid: path === "pi" ? piData?.piUid || null : null,
            piUsername: path === "pi" ? piData?.username || null : null,
            signUpMethod: path,
            lastMiningDate: null,
            referalsCount: 0,
            stakeBoost: 1,
            referredBy: referrerId,
            lastLoginDate: serverTimestamp(),
            currentStreak: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };

          // Set the new user document in Firestore
          transaction.set(doc(usersCollection, newUserId), newUser);

          // Welcome bonus transaction record removed to reduce Firestore write costs

          const miningInfos: MiningInfos = {
            baseGrant: arthereumTokenomics.calculateBaseGrant(),
            nextGrantValue: arthereumTokenomics.calculateNextGrantValue(0, 1),
            nextGrantDate: new Date(),
            miningReferredUserCount: 0,
          };

          return { user: newUser as any, miningInfos };
        }
      );

      return result;
    } catch (error: any) {
      if (
        [
          "Email already in use.",
          "Username already taken.",
          "This Pi Network user already has an account.",
        ].includes(error.message)
      ) {
        throw new APIError(error.message, 400);
      }
      console.error("An unexpected error occurred during sign up:", error);
      throw new APIError(error.message || "An unexpected error occurred", 500);
    }
  }

  /**
   * Claims the 2-hour grant.
   * `adsWatched` = verified rewarded ads watched in the pre-claim modal (0, 1 or 2)
   * → 1x, 1.5x, 2x (GRANT_AD_BOOSTS). Every multiplier is stacked then capped
   * at MAX_GRANT_MULTIPLIER.
   */
  async acceptGrant(
    userUid: string,
    adsWatched: number = 0
  ): Promise<{ grantValue: number, multiplier: number, piGiveaway: number }> {
    const adBoost = GRANT_AD_BOOSTS[Math.max(0, Math.min(2, Math.floor(adsWatched || 0)))] ?? 1;
    try {
      // Step 1: countRecentMiners cannot run inside a transaction, so fetch it first.
      const userRef = doc(db, "athUsers", userUid);
      const recentMinersCount = await countRecentMiners(userUid);

      // Step 2: Run the atomic transaction — all user reads happen here (single read).
      let result = { grantValue: 0, multiplier: 1, piGiveaway: 0 };
      await runTransaction(db, async (transaction) => {
        const transactionalUserDoc = await transaction.get(userRef);

        if (!transactionalUserDoc.exists()) {
          throw new APIError("User not found", 404);
        }

        const userData = transactionalUserDoc.data();

        // Compute grant value using transactional data
        let grantValue = arthereumTokenomics.calculateNextGrantValue(
          recentMinersCount,
          userData.stakeBoost || 1
        );

        const now = new Date();
        const active = (ts: any) => !!ts?.toDate && (ts as Timestamp).toDate() > now;

        // Multiplier stack (product capped at MAX_GRANT_MULTIPLIER)
        let mult = adBoost; // 1 / 1.5 / 2 from verified pre-claim ads
        const currentStreak = userData.currentStreak || 0;
        mult *= 1 + Math.min(0.2, currentStreak * 0.01); // streak: +1%/day, max +20%
        if (active(userData.luckyTagExpiry)) mult *= 2; // Lucky Tag (1h)
        if (active(userData.directBoostExpiry)) mult *= 2; // Direct Boost (next claim)
        if (isProData(userData)) mult *= 2; // Pro
        if (active(userData.turboChargeExpiry)) mult *= 5; // Turbo (1h / 24h)
        if (active(userData.flashBoostExpiry)) mult *= 10; // Flash Boost (24h / 48h)
        mult = Math.min(mult, MAX_GRANT_MULTIPLIER);
        grantValue *= mult;

        // 2-hour cooldown between grants. Boosts multiply the grant, they never skip the wait.
        if (userData.lastMiningDate) {
          const lastMining = userData.lastMiningDate.toDate();
          const diffHours =
            Math.abs(now.getTime() - lastMining.getTime()) / 36e5;
          if (diffHours < 2) {
            throw new Error("Need to wait 2 hours between grants");
          }
        }

        // --- VIRAL GROWTH ENGINE: TIERED COMMISSIONS ---
        // Tier 1: Direct Referrer (10%)
        if (userData.referredBy) {
          const referrerRef = doc(db, "athUsers", userData.referredBy);
          const referrerDoc = await transaction.get(referrerRef);
          
          if (referrerDoc.exists()) {
             const commission = grantValue * 0.10;
             const updates: any = {
               arthereumBalance: increment(commission)
             };

             // Invite-to-Spin: Free Lucky Spin if it's the referral's first mining session
             if (userData.miningSessionsCount === 0) {
                updates.luckySpinCount = increment(1);
             }

             transaction.update(referrerRef, updates);
             
             // Tier 2: Referrer's Referrer (5%)
             const referrerData = referrerDoc.data();
             if (referrerData.referredBy) {
               const tier2Ref = doc(db, "athUsers", referrerData.referredBy);
               const tier2Commission = grantValue * 0.05;
               transaction.update(tier2Ref, {
                 arthereumBalance: increment(tier2Commission)
               });
             }
          }
        }

        // The Pi winter give away: 1/100 of the final grantValue in Pi
        const piGiveaway = grantValue / 100;

        // Update user's balance and mining stats.
        const updateData: any = {
          arthereumBalance: increment(grantValue),
          picoinBalance: increment(piGiveaway),
          miningSessionsCount: increment(1),
          lastMiningDate: serverTimestamp(),
        };

        // Reset Direct Boost after use
        if (userData.directBoostExpiry) {
          const expiry = (userData.directBoostExpiry as Timestamp).toDate();
          if (expiry > new Date()) {
            updateData.directBoostExpiry = null;
          }
        }

        transaction.update(userRef, updateData);

        // Grant transaction record removed to reduce Firestore write costs
        // (was creating ~12 docs/user/day = ~30K docs/day)

        // Store result for return
        const baseGrantValue = arthereumTokenomics.calculateNextGrantValue(
          recentMinersCount,
          userData.stakeBoost || 1
        );
        result = { grantValue, multiplier: baseGrantValue > 0 ? grantValue / baseGrantValue : 1, piGiveaway };
      });

      return result;
    } catch (error) {
      console.error("Error accepting grant:", error);
      if (error instanceof APIError) {
        throw error;
      }
      if (error instanceof Error) {
        // Translate Firestore errors into the app's custom APIError format.
        throw new APIError(error.message, 400);
      }
      throw new APIError("An unexpected error occurred while accepting grant");
    }
  }

  async activateLuckyTag(userId: string): Promise<void> {
    try {
      const userRef = doc(db, "athUsers", userId);
      const expiry = new Date(Date.now() + 60 * 60 * 1000); // 1 hour
      await runTransaction(db, async (transaction) => {
        transaction.update(userRef, {
          luckyTagExpiry: Timestamp.fromDate(expiry),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error("Error activating lucky tag:", error);
      throw new APIError("Failed to activate lucky tag");
    }
  }

  async activateDirectBoost(userId: string): Promise<void> {
    try {
      const userRef = doc(db, "athUsers", userId);
      const expiry = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours or until claim
      await runTransaction(db, async (transaction) => {
        transaction.update(userRef, {
          directBoostExpiry: Timestamp.fromDate(expiry),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      console.error("Error activating direct boost:", error);
      throw new APIError("Failed to activate direct boost");
    }
  }

  async activatePro(userId: string, piAmount: number): Promise<void> {
    // Safety check: Ensure consistent pricing (500 Pi)
    if (piAmount < 500) {
      throw new APIError("Invalid price for Pro subscription. Minimal price is 500 Pi.", 400);
    }
    try {
      const userRef = doc(db, "athUsers", userId);
      // Subscription for 30 days
      const expiry = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();
        if ((userData.picoinBalance || 0) < piAmount) {
          throw new Error("Insufficient Pi balance");
        }

        transaction.update(userRef, {
          isPro: true,
          proExpiry: Timestamp.fromDate(expiry),
          picoinBalance: increment(-piAmount),
          updatedAt: serverTimestamp(),
        });

        // Pro subscription transaction record removed to reduce Firestore write costs
      });
    } catch (error) {
      console.error("Error activating Pro:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to activate Arthereum Pro");
    }
  }

  async activateLifetimePro(userId: string, piAmount: number): Promise<void> {
    // Safety check: Ensure consistent pricing (5000 Pi for lifetime)
    if (piAmount < 5000) {
      throw new APIError("Invalid price for Lifetime Pro. Minimal price is 5000 Pi.", 400);
    }
    try {
      const userRef = doc(db, "athUsers", userId);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();
        
        // Check if already lifetime pro
        if (userData.isLifetimePro) {
          throw new Error("You already have Lifetime Pro!");
        }

        if ((userData.picoinBalance || 0) < piAmount) {
          throw new Error("Insufficient Pi balance");
        }

        transaction.update(userRef, {
          isPro: true,
          isLifetimePro: true,
          proExpiry: null, // Lifetime = no expiry
          picoinBalance: increment(-piAmount),
          updatedAt: serverTimestamp(),
        });

        // Lifetime Pro transaction record removed to reduce Firestore write costs
      });
    } catch (error) {
      console.error("Error activating Lifetime Pro:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to activate Lifetime Pro");
    }
  }

  /**
   * Buys a Forge boost. The price comes from FORGE_PRICES, never from the caller.
   */
  async purchaseForgeItem(userId: string, itemId: string): Promise<number> {
    const piAmount = FORGE_PRICES[itemId];
    if (piAmount === undefined) throw new APIError("Unknown forge item");
    try {
      const userRef = doc(db, "athUsers", userId);

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();
        if ((userData.picoinBalance || 0) < piAmount) {
          throw new Error("Insufficient Pi balance");
        }

        // Apply boost based on item type
        const now = new Date();
        let updateData: any = {
          picoinBalance: increment(-piAmount),
          updatedAt: serverTimestamp(),
        };

        switch (itemId) {
          case "streak_resurrection":
            // Restore streak to the last known value (or 7 if unknown)
            updateData.currentStreak = userData.previousStreak || 7;
            break;
          case "flash_boost":
            updateData.flashBoostExpiry = Timestamp.fromDate(
              new Date(now.getTime() + 24 * 60 * 60 * 1000)
            );
            break;
          case "turbo_charge":
            updateData.turboChargeExpiry = Timestamp.fromDate(
              new Date(now.getTime() + 24 * 60 * 60 * 1000)
            );
            break;
          case "instant_refill":
            updateData.lastMiningDate = null;
            break;
          case "mega_flash_boost":
            updateData.flashBoostExpiry = Timestamp.fromDate(
              new Date(now.getTime() + 48 * 60 * 60 * 1000)
            );
            break;
          default:
            throw new Error("Unknown forge item");
        }

        transaction.update(userRef, updateData);

        // Forge purchase transaction record removed to reduce Firestore write costs
      });
      return piAmount;
    } catch (error) {
      console.error("Error purchasing forge item:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to purchase forge item");
    }
  }

  /**
   * Opens a mystery box. The price comes from BOX_PRICES, never from the caller.
   * `freeViaAds` is only honoured for the bronze box (the "watch 3 ads" path).
   */
  async openMysteryBox(
    userId: string,
    boxId: string,
    freeViaAds: boolean = false
  ): Promise<{ reward: string; amount: number; type: string; rarity: string; price: number }> {
    const listedPrice = BOX_PRICES[boxId];
    if (listedPrice === undefined) throw new APIError("Unknown mystery box");
    const piAmount = freeViaAds && boxId === "bronze_box" ? 0 : listedPrice;
    try {
      const userRef = doc(db, "athUsers", userId);
      let result: { reward: string; amount: number; type: string; rarity: string; price: number } = {
        reward: "",
        amount: 0,
        type: "pi",
        rarity: "common",
        price: piAmount,
      };

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();
        if ((userData.picoinBalance || 0) < piAmount) {
          throw new Error("Insufficient Pi balance");
        }
        if (freeViaAds && (userData.freeBoxAdsCount || 0) < FREE_BOX_ADS_REQUIRED) {
          throw new Error(`Watch ${FREE_BOX_ADS_REQUIRED} ads to open a free Bronze box`);
        }

        // Generate random reward based on box tier
        const random = Math.random();
        let rewardType: "pi" | "ath" | "boost";
        let rewardAmount: number;
        let rarity: string = "common";

        switch (boxId) {
          case "bronze_box":
            if (random < 0.7) {
              rewardType = "pi";
              rewardAmount = 0.5 + Math.random() * 1.5;
            } else if (random < 0.95) {
              rewardType = "ath";
              rewardAmount = 50 + Math.floor(Math.random() * 150);
            } else {
              rewardType = "boost";
              rewardAmount = 1;
              rarity = "rare";
            }
            break;
          case "silver_box":
            rarity = "rare";
            if (random < 0.6) {
              rewardType = "pi";
              rewardAmount = 1 + Math.random() * 4;
            } else if (random < 0.9) {
              rewardType = "ath";
              rewardAmount = 200 + Math.floor(Math.random() * 800);
            } else {
              rewardType = "boost";
              rewardAmount = 1;
            }
            break;
          case "gold_box":
            rarity = "epic";
            if (random < 0.5) {
              rewardType = "pi";
              rewardAmount = 2 + Math.random() * 8;
            } else if (random < 0.85) {
              rewardType = "ath";
              rewardAmount = 500 + Math.floor(Math.random() * 4500);
            } else {
              rewardType = "boost";
              rewardAmount = 1;
            }
            break;
          case "diamond_box":
            rarity = random < 0.05 ? "legendary" : "epic";
            if (random < 0.05) {
              // JACKPOT!
              rewardType = "pi";
              rewardAmount = 20 + Math.random() * 30;
              rarity = "legendary";
            } else if (random < 0.4) {
              rewardType = "pi";
              rewardAmount = 5 + Math.random() * 15;
            } else if (random < 0.75) {
              rewardType = "ath";
              rewardAmount = 2000 + Math.floor(Math.random() * 18000);
            } else {
              rewardType = "boost";
              rewardAmount = 1;
            }
            break;
          case "whale_box":
            // Whale Box — 500 Pi, highest tier
            if (random < 0.10) {
              // MEGA JACKPOT! 10% chance
              rewardType = "pi";
              rewardAmount = 500 + Math.random() * 500; // 500-1000 Pi refund + profit
              rarity = "legendary";
            } else if (random < 0.45) {
              // 35% chance — Pi reward
              rewardType = "pi";
              rewardAmount = 50 + Math.random() * 150; // 50-200 Pi
              rarity = "epic";
            } else if (random < 0.75) {
              // 30% chance — ATH reward
              rewardType = "ath";
              rewardAmount = 50000 + Math.floor(Math.random() * 150000); // 50K-200K ATH
              rarity = "epic";
            } else {
              // 25% chance — Boost
              rewardType = "boost";
              rewardAmount = 1;
              rarity = "rare";
            }
            break;
          default:
            throw new Error("Unknown mystery box");
        }

        rewardAmount = parseFloat(rewardAmount.toFixed(2));

        // Update user balance
        let updateData: any = {
          picoinBalance: increment(-piAmount),
          updatedAt: serverTimestamp(),
        };
        if (freeViaAds) updateData.freeBoxAdsCount = 0;

        if (rewardType === "pi") {
          updateData.picoinBalance = increment(rewardAmount - piAmount);
        } else if (rewardType === "ath") {
          updateData.arthereumBalance = increment(rewardAmount);
        } else if (rewardType === "boost") {
          updateData.flashBoostExpiry = Timestamp.fromDate(
            new Date(Date.now() + 24 * 60 * 60 * 1000)
          );
        }

        transaction.update(userRef, updateData);

        // Mystery box transaction record removed to reduce Firestore write costs

        result = {
          reward: rewardType === "boost" ? "Flash Boost" : `${rewardAmount}`,
          amount: rewardAmount,
          type: rewardType,
          rarity: rarity,
          price: piAmount,
        };
      });

      return result;
    } catch (error) {
      console.error("Error opening mystery box:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to open mystery box");
    }
  }

  /**
   * Credits ATH earned from a game (bucket "games", GAMES_DAILY_ATH_CAP) or from
   * an Ad Hub ad (gameId "watch_and_earn", bucket "ads", ADS_DAILY_ATH_CAP).
   * Never throws when a cap is hit — returns what was actually credited so the
   * UI can be honest. Rounds/ads themselves are never limited.
   */
  async awardGameATH(
    userId: string,
    gameId: string,
    athAmount: number,
    score: number
  ): Promise<{ credited: number; todayTotal: number; dailyCap: number }> {
    const isAds = gameId === "watch_and_earn";
    const dailyCap = isAds ? ADS_DAILY_ATH_CAP : GAMES_DAILY_ATH_CAP;
    const dateField = isAds ? "lastAdRewardDate" : "lastGameRewardDate";
    const totalField = isAds ? "todayAdRewards" : "todayGameRewards";
    try {
      const userRef = doc(db, "athUsers", userId);
      let outcome = { credited: 0, todayTotal: 0, dailyCap };

      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();

        const today = todayKey();
        const todayTotal = userData[dateField] === today ? (userData[totalField] || 0) : 0;

        const requested = Math.max(0, Number(athAmount) || 0);
        const actualReward = parseFloat(
          Math.max(0, Math.min(requested, dailyCap - todayTotal)).toFixed(4)
        );

        outcome = {
          credited: actualReward,
          todayTotal: todayTotal + actualReward,
          dailyCap,
        };

        transaction.update(userRef, {
          arthereumBalance: increment(actualReward),
          [dateField]: today,
          [totalField]: todayTotal + actualReward,
          updatedAt: serverTimestamp(),
        });

        // Game reward transaction record removed to reduce Firestore write costs

        // Increment daily stats for quests
        const lastUpdate = userData.dailyStats?.lastUpdate || "";
        let dailyStats = userData.dailyStats || { gamesPlayed: 0, sharesCount: 0, lastUpdate: today };
        
        if (lastUpdate !== today) {
          dailyStats = { gamesPlayed: 0, sharesCount: 0, lastUpdate: today };
        }
        
        dailyStats.gamesPlayed += 1;
        dailyStats.lastUpdate = today;
        
        transaction.update(userRef, {
          dailyStats: dailyStats,
          updatedAt: serverTimestamp()
        });
      });

      return outcome;
    } catch (error) {
      console.error("Error awarding game ATH:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to award game reward");
    }
  }

  /**
   * Resets the grant cooldown ("skip the wait").
   * - "ads": the user watched the required verified ads (n-th skip of the day = n ads) → free
   * - "pro": Pro members get their first skip of the day free
   * - "pi":  costs INSTANT_REFILL_COST Pi
   * SKIPS_PER_DAY caps the number of skips (an economy cap, not an ad cap).
   */
  async instantRefill(
    userId: string,
    mode: "ads" | "pro" | "pi" | boolean = "ads"
  ): Promise<{ skipsToday: number }> {
    const resolved: "ads" | "pro" | "pi" = mode === true ? "ads" : mode === false ? "pi" : mode;
    const piAmount = resolved === "pi" ? INSTANT_REFILL_COST : 0;
    let skipsAfter = 0;
    try {
      const userRef = doc(db, "athUsers", userId);
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data();
        const today = todayKey();
        const skipsToday = userData.skipsTodayDate === today ? userData.skipsTodayCount || 0 : 0;
        if (skipsToday >= SKIPS_PER_DAY) {
          throw new Error(`Daily skip limit reached (${SKIPS_PER_DAY}/day)`);
        }
        if (resolved === "pro" && (!isProData(userData) || skipsToday > 0)) {
          throw new Error("Free Pro skip already used today");
        }
        if ((userData.picoinBalance || 0) < piAmount) {
          throw new Error("Insufficient Pi balance");
        }
        skipsAfter = skipsToday + 1;

        transaction.update(userRef, {
          lastMiningDate: null, // Reset cooldown
          picoinBalance: increment(-piAmount),
          skipsTodayDate: today,
          skipsTodayCount: skipsAfter,
          updatedAt: serverTimestamp(),
        });

        // Instant refill transaction record removed to reduce Firestore write costs
      });
      return { skipsToday: skipsAfter };
    } catch (error) {
      console.error("Error instant refill:", error);
      if (error instanceof Error) throw new APIError(error.message);
      throw new APIError("Failed to refill grant");
    }
  }


  async getLeaderboard(limitCount: number = 10): Promise<User[]> {
    // Return cached data if still fresh (5 min TTL)
    if (this.leaderboardCache && Date.now() - this.leaderboardCache.timestamp < this.CACHE_TTL) {
      return this.leaderboardCache.data.slice(0, limitCount);
    }

    const usersCollection = collection(db, "athUsers");
    const q = query(
      usersCollection,
      orderBy("arthereumBalance", "desc"),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const results = querySnapshot.docs.map((doc) => doc.data() as User);

    // Cache the results
    this.leaderboardCache = { data: results, timestamp: Date.now() };
    return results;
  }

  async logout(userUid: string): Promise<void> {
    try {
      // Local only: logging out must not touch the grant cooldown
      // (resetting lastMiningDate here allowed unlimited grants via logout/login loops).
      await this.removeToken();
    } catch (error) {
      console.error("Logout failed:", error);
      if (error instanceof Error) {
        throw new APIError(error.message);
      }
      throw new APIError("An unexpected error occurred during logout");
    }
  }
  async getTransactionHistory(
    userId: string,
    page: DocumentSnapshot | null,
    limitt: number = 10,
    crypto?: string,
    withUserId?: string
  ): Promise<{
    transactions: any[];
    hasMore: boolean;
    lastDoc: DocumentSnapshot | null;
  }> {
    const createCombinedId = (id1: string, id2: string): string => {
      return [id1, id2].sort().join("_");
    };

    try {
      const transactionsCollection = collection(db, "athTransactions2");
      let constraints: any[] = [
        where("usersInvolvedIds", "array-contains", userId),
      ];

      if (crypto) {
        if (crypto === "*") {
          constraints.push(where("crypto", "!=", null));
        } else {
          constraints.push(where("crypto", "==", crypto.trim().toLowerCase()));
        }
      } else if (withUserId) {
        constraints.push(
          where("combinedUsersId", "==", createCombinedId(userId, withUserId))
        );
      }

      constraints.push(orderBy("date", "desc"));

      if (page) {
        constraints.push(startAfter(page));
      }

      constraints.push(limit(limitt));

      const paginatedQuery = query(transactionsCollection, ...constraints);
      const querySnapshot = await getDocs(paginatedQuery);
      const transactions = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          date: data.date?.toDate
            ? data.date.toDate().toISOString()
            : data.date,
        };
      });

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      const hasMore = querySnapshot.docs.length === limitt;

      return { transactions, hasMore, lastDoc };
    } catch (error) {
      console.error(
        "Error fetching transactions directly from Firestore:",
        error
      );
      if (error instanceof Error) {
        throw new Error(`Failed to fetch transactions: ${error.message}`);
      }
      throw new Error(
        "An unexpected error occurred while fetching transactions"
      );
    }
  }
  async sendVerificationEmail(): Promise<void> {
    try {
    } catch (error) {}
  }

  async verifyEmail(code: string): Promise<void> {}
  async stakePI(
    userUid: string,
    amount: string,
    transactionHash?: string
  ): Promise<{ newStakeBoost: number; stakeAmount: number }> {
    // Helper function to verify a Pi transaction by calling its public API
    const verifyPITransactionHash = async (txHash: string): Promise<number> => {
      try {
        const url = `https://api.mainnet.minepi.com/transactions/${txHash}/operations?limit=10&order=desc`;
        const response = await axios.get(url);
        const operations = response.data._embedded.records;
        // Sum up the amounts from the transaction operations
        return operations.reduce(
          (total: number, op: any) => total + parseFloat(op.amount),
          0
        );
      } catch (error) {
        console.error("Error verifying Pi transaction hash:", error);
        throw new Error("Invalid Pi transaction hash");
      }
    };

    // Helper to define a stake period. The backend currently only uses "1_month".
    const getStakeTimePeriodInMonths = (timePeriod: "1_month"): number => 1;

    // Helper to create a consistent combined ID for two users
    const createCombinedId = (id1: string, id2: string): string => {
      return [id1, id2].sort().join("_");
    };

    try {
      // Use Firestore's `runTransaction` to ensure all database writes succeed or fail together
      const stakeBoostData = await runTransaction(db, async (transaction) => {
        let stakeAmount = parseFloat(amount);
        const userRef = doc(db, "athUsers", userUid);

        // If a transaction hash is provided, verify it and check for double-spending
        if (transactionHash) {
          const stakeRef = doc(db, "athStakes", transactionHash);
          const stakeDoc = await transaction.get(stakeRef);
          if (stakeDoc.exists()) {
            throw new Error("This transaction hash has already been consumed");
          }
          // The actual amount is fetched from the Pi network API for security
          stakeAmount = await verifyPITransactionHash(transactionHash);
        }

        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) {
          throw new Error("User not found");
        }

        // Validate user's balance if staking from their in-app balance
        if (!transactionHash && userDoc.data().picoinBalance < stakeAmount) {
          throw new Error("Insufficient pi balance");
        }

        if (stakeAmount < 0.1) {
          throw new Error("The amount is too low. Minimum amount is 0.1");
        }

        const stakeTimePeriod = getStakeTimePeriodInMonths("1_month");
        // Assumes `calculateStakeBoost` is available from your arthereumTokenomics instance
        // Boost: +1x per STAKE_PI_PER_1X π, total capped at 1 + STAKE_BOOST_MAX_EXTRA
        const currentBoost = userDoc.data().stakeBoost || 1;
        const newStakeBoost = Math.max(
          0,
          Math.min(
            arthereumTokenomics.calculateStakeBoost(stakeAmount, stakeTimePeriod),
            1 + STAKE_BOOST_MAX_EXTRA - currentBoost
          )
        );

        // 1. Update the user's document
        transaction.update(userRef, {
          stakeBoost: increment(newStakeBoost),
          picoinBalance: increment(-stakeAmount),
        });

        const stakeId = transactionHash ? transactionHash : v4();
        const endsAt = new Date(
          Date.now() + stakeTimePeriod * 30 * 24 * 60 * 60 * 1000
        );
        let transactionId: string | undefined = undefined;

        // 2. Create a transaction record if staking from in-app balance
        if (!transactionHash) {
          transactionId = v4();
          const depositTransactionRef = doc(
            db,
            "athTransactions2",
            transactionId
          );
          transaction.set(depositTransactionRef, {
            id: transactionId,
            type: "stake",
            crypto: "picoin",
            amount: stakeAmount,
            from: userUid,
            to: "arthereum_app",
            usersInvolvedIds: [userUid, "arthereum_app"],
            combinedUsersId: createCombinedId(userUid, "arthereum_app"),
            revoked: false,
            date: serverTimestamp(),
          });
        }

        // 3. Create the new stake document
        const stakeRef = doc(db, "athStakes", stakeId);
        transaction.set(stakeRef, {
          id: stakeId,
          userUid: userUid,
          stakeBoost: newStakeBoost,
          piAmountStaked: stakeAmount,
          period: stakeTimePeriod,
          createdAt: serverTimestamp(),
          endsAt: endsAt,
          unstaked: false,
          transactionHash: transactionHash ?? null,
          transactionId: transactionId,
        });

        return { newStakeBoost, stakeAmount };
      });

      return stakeBoostData; // Return the result on success
    } catch (error) {
      console.error("Frontend Error staking PI:", error);
      // The calling function can catch this error and display the message
      throw error;
    }
  }
  async getStakes(
    userId: string,
    page: DocumentSnapshot | null,
    limitt: number = 10
  ): Promise<{
    stakes: any[];
    hasMore: boolean;
    statistics: any;
    lastDoc: DocumentSnapshot | null;
  }> {
    try {
      const stakesCollection = collection(db, "athStakes");
      const countQuery = query(
        stakesCollection,
        where("userUid", "==", userId)
      );
      const countSnapshot = await getCountFromServer(countQuery);
      const totalCount = countSnapshot.data().count;

      let stakesQuery;
      if (page) {
        stakesQuery = query(
          stakesCollection,
          where("userUid", "==", userId),
          orderBy("createdAt", "desc"),
          limit(limitt),
          startAfter(page)
        );
      } else {
        stakesQuery = query(
          stakesCollection,
          where("userUid", "==", userId),
          orderBy("createdAt", "desc"),
          limit(limitt)
        );
      }

      const stakesSnapshot = await getDocs(stakesQuery);
      const stakes = stakesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          createdAt: data.createdAt?.toDate().toISOString(),
          endsAt: data.endsAt?.toDate().toISOString(),
        };
      });

      // Use getCountFromServer instead of fetching all active stake docs
      const activeStakesCountQuery = query(
        stakesCollection,
        where("userUid", "==", userId),
        where("unstaked", "==", false)
      );
      const activeCountSnapshot = await getCountFromServer(activeStakesCountQuery);
      const activeStakesCount = activeCountSnapshot.data().count;

      const statistics = {
        totalPiStaked: 0, // Computed server-side if needed
        totalBoost: 0,
        activeStakesCount,
        totalStakesCount: totalCount,
      };

      const lastDoc =
        stakesSnapshot.docs[stakesSnapshot.docs.length - 1] || null;
      const hasMore = stakes.length === limitt;

      return { stakes, hasMore, statistics, lastDoc };
    } catch (error) {
      console.error("Error fetching stakes directly from Firestore:", error);
      throw new Error("Failed to fetch stakes from Firestore.");
    }
  }
  async checkIfUsernameIsAvailable(
    username: string
  ): Promise<{ available: boolean; user: any | null }> {
    try {
      const formattedUsername = username?.trim()?.toLowerCase();
      if (!formattedUsername) {
        // Replicates the backend check for a missing username
        return { available: false, user: null };
      }

      // Construct a query to find a user with the same lowercase username
      const usersCollectionRef = collection(db, "athUsers");
      const q = query(
        usersCollectionRef,
        where("lowercasedUsername", "==", formattedUsername),
        limit(1)
      );

      // Execute the query
      const querySnapshot = await getDocs(q);

      // If the snapshot is empty, the username is available
      if (querySnapshot.empty) {
        return { available: true, user: null };
      } else {
        // Otherwise, it's taken
        return {
          available: false,
          user: querySnapshot.docs[0].data(),
        };
      }
    } catch (error) {
      console.error("Error checking username availability:", error);
      throw new Error(
        "An unexpected error occurred while checking username availability."
      );
    }
  }
  async getUserReferrals(
    userUid: string,
    offsetParam: DocumentSnapshot | null
  ): Promise<{
    referrals: User[];
    lastDoc: DocumentSnapshot | null;
    hasMore: boolean;
  }> {
    try {
      const queryLimit = 10;
      let referralsQuery;

      if (offsetParam) {
        referralsQuery = query(
          collection(db, "athUsers"),
          where("referredBy", "==", userUid),
          orderBy("createdAt", "desc"),
          startAfter(offsetParam),
          limit(queryLimit)
        );
      } else {
        referralsQuery = query(
          collection(db, "athUsers"),
          where("referredBy", "==", userUid),
          orderBy("createdAt", "desc"),
          limit(queryLimit)
        );
      }

      const querySnapshot = await getDocs(referralsQuery);
      const referrals = querySnapshot.docs.map((doc) => {
        const userData = doc.data();
        const { password, picoinBalance, ...safeUser } = userData;
        return safeUser as User;
      });

      const lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
      const hasMore = referrals.length === queryLimit;

      return { referrals, lastDoc, hasMore };
    } catch (error) {
      console.error("Error fetching referrals directly from Firestore:", error);
      throw new Error("An unexpected error occurred while fetching referrals.");
    }
  }

  async getTopInviters(limitCount: number = 5): Promise<User[]> {
    try {
      const usersCollection = collection(db, "athUsers");
      const topInvitersQuery = query(
        usersCollection,
        orderBy("referalsCount", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(topInvitersQuery);
      return querySnapshot.docs.map((doc) => {
        const userData = doc.data();
        const { password, picoinBalance, ...safeUser } = userData;
        return safeUser as User;
      });
    } catch (error) {
      console.error("Error fetching top inviters:", error);
      return [];
    }
  }
  // async getLeaderboard(
  //   sortBy: LeaderboardSortBy = "arthereumBalance",
  //   sortOrder: LeaderboardSortDirection = "desc",
  //   page: number = 1,
  //   limit: number = 10
  // ): Promise<{
  //   users: any[];
  //   hasMore: boolean;
  //   totalUsers: number;
  // }> {
  //   try {
  //     const response = await AxiosInstance.get("/leaderboard", {
  //       params: { sortBy, sortOrder, page, limit },
  //     });
  //     return response.data;
  //   } catch (error) {
  //     if (axios.isAxiosError(error)) {
  //       const axiosError = error as AxiosError;
  //       throw new APIError(
  //         (axiosError.response?.data as any)?.message ||
  //           "Failed to fetch leaderboard",
  //         axiosError.response?.status,
  //         axiosError.response?.data as any
  //       );
  //     }
  //     throw new APIError(
  //       "An unexpected error occurred while fetching leaderboard"
  //     );
  //   }
  // }
  async processTransaction(
    transactionData: TransactionData
  ): Promise<UserModelData> {
    const { from, toUsername, amount, crypto } = transactionData;
    const supportedCryptos = ["picoin"];

    // 1. Basic validation, moved from the backend
    if (!supportedCryptos.includes(crypto?.trim()?.toLowerCase())) {
      throw new APIError("Unsupported cryptocurrency");
    }

    try {
      // 2. Use Firestore's transaction functionality
      await runTransaction(db, async (transaction) => {
        // Get references to the sender and recipient
        const fromUserRef = doc(db, "athUsers", from);
        const toUserQuery = query(
          collection(db, "athUsers"),
          where("lowercasedUsername", "==", toUsername.trim()?.toLowerCase()),
          limit(1)
        );

        // Fetch documents within the transaction
        const fromUserDoc = await transaction.get(fromUserRef);
        const toUserSnapshot = await getDocs(toUserQuery);

        if (!fromUserDoc.exists()) {
          throw new Error("Sender not found");
        }
        if (toUserSnapshot.empty) {
          throw new Error("Recipient not found");
        }

        const fromUserData = fromUserDoc.data();
        const toUserDoc = toUserSnapshot.docs[0];
        const toUserData = toUserDoc.data();
        const balanceField = `${crypto.trim().toLowerCase()}Balance`;

        // Check for sufficient balance
        if (fromUserData[balanceField] < amount) {
          throw new Error("Insufficient balance");
        }

        // 3. Perform the atomic updates
        // Decrement sender's balance
        transaction.update(fromUserRef, {
          [balanceField]: increment(-amount),
        });

        // Increment recipient's balance
        transaction.update(toUserDoc.ref, {
          [balanceField]: increment(amount),
        });

        // 4. Record the transaction
        const transactionId = v4(); // Assuming uuid is available
        const newTransactionRef = doc(db, "athTransactions2", transactionId);
        const createCombinedId = (id1: string, id2: string) =>
          [id1, id2].sort().join("_");

        transaction.set(newTransactionRef, {
          id: transactionId,
          from: from,
          to: toUserData.id,
          combinedUsersId: createCombinedId(toUserData.id, from),
          usersInvolvedIds: [toUserData.id, from],
          amount: amount,
          type: "transfer",
          crypto: crypto.trim().toLowerCase(),
          date: serverTimestamp(),
        });
      });

      // NOTE: Kept the refetch here because callers (GlobalModalsDisplayer) use
      // the full user object for Redux state. sendCrypto is rare (<1/user/day).
      const updatedUserDoc = await getDoc(doc(db, "athUsers", from));
      const user = updatedUserDoc.data() as User;
      const miningReferredUserCount = await countRecentMiners(user.id);

      const miningInfos = {
        baseGrant: arthereumTokenomics.calculateBaseGrant(),
        nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
          miningReferredUserCount,
          user.stakeBoost
        ),
        miningReferredUserCount,
        nextGrantDate: user.lastMiningDate
          ? getNextGrantDate(user.lastMiningDate.toDate())
          : new Date(),
      };

      return { user: user as any, miningInfos };
    } catch (error: any) {
      // 6. Error handling
      throw new APIError(
        error.message || "An unexpected error occurred during the transaction."
      );
    }
  }
  async unstake(stakeId: string, userUid: string): Promise<UserModelData> {
    // Helper functions migrated from the backend
    const getDiffInDaysFromDates = (date1: Date, date2: Date): number => {
      const msPerDay = 1000 * 60 * 60 * 24;
      const diffInMs = Math.abs(date1.getTime() - date2.getTime());
      return Math.floor(diffInMs / msPerDay);
    };

    const calculateUnstakeAmount = (
      initialAmount: number,
      stakeDaysLeft: number,
      stakeDurationInDays: number = 30
    ): number => {
      if (stakeDaysLeft <= stakeDurationInDays - 10) {
        return 0; // No return if unstaked after 10 days
      }
      const penaltyFactor = Math.pow(stakeDaysLeft / stakeDurationInDays, 2);
      const maxReturn = initialAmount / 2;
      const unstakeAmount = maxReturn * (1 - penaltyFactor);
      return Math.max(unstakeAmount, 0);
    };

    try {
      // 1. Run the entire logic in a transaction
      await runTransaction(db, async (transaction) => {
        const stakeRef = doc(db, "athStakes", stakeId);
        const userRef = doc(db, "athUsers", userUid);

        const stakeDoc = await transaction.get(stakeRef);
        const userDoc = await transaction.get(userRef);

        if (!stakeDoc.exists()) throw new Error("Stake not found");
        if (!userDoc.exists()) throw new Error("User not found");

        const stakeData = stakeDoc.data();
        const userData = userDoc.data();

        if (stakeData.userUid !== userUid)
          throw new Error("Unauthorized access to stake");
        if (stakeData.unstaked) throw new Error("Stake already unstaked");

        const now = new Date();
        const endsAt = stakeData.endsAt.toDate();

        // 2. Handle early unstaking penalty
        if (now < endsAt) {
          const daysLeft = getDiffInDaysFromDates(now, endsAt);
          // The backend logic checks for `daysLeft >= 20`. Let's stick to that.
          if (daysLeft >= 20) {
            const amountToReturn = calculateUnstakeAmount(
              stakeData.piAmountStaked,
              daysLeft,
              stakeData.period * 30
            );
            if (amountToReturn > 0) {
              transaction.update(userRef, {
                picoinBalance: increment(Number(amountToReturn.toFixed(4))),
              });
            }
          }
        }

        // 3. Update user's stakeBoost
        // Note: The backend had a slight bug `stakeData.stakeBoost - 1` when it should just be `stakeData.stakeBoost`. Correcting it here.
        transaction.update(userRef, {
          stakeBoost: Math.max(1, userData.stakeBoost - stakeData.stakeBoost),
        });

        // 4. Mark stake as unstaked
        transaction.update(stakeRef, {
          unstaked: true,
          unstakedAt: serverTimestamp(),
        });
      });

      // NOTE: Kept the refetch here because callers (GlobalModalsDisplayer) use
      // user?.user?.stakeBoost for UI display. unstake is rare (<1/user/day).
      const updatedUserDoc = await getDoc(doc(db, "athUsers", userUid));
      const user = updatedUserDoc.data() as User;
      const miningReferredUserCount = await countRecentMiners(user.id);

      const miningInfos = {
        baseGrant: arthereumTokenomics.calculateBaseGrant(),
        nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
          miningReferredUserCount,
          user.stakeBoost
        ),
        miningReferredUserCount,
        nextGrantDate: user.lastMiningDate
          ? getNextGrantDate(user.lastMiningDate.toDate())
          : new Date(),
      };

      return { user: user as any, miningInfos };
    } catch (error: any) {
      throw new APIError(
        error.message || "An unexpected error occurred during unstaking."
      );
    }
  }
  /**
   * Credits ONE verified rewarded ad (any placement): dedupes on adId, counts
   * today's ads on the user doc (`adsTodayCount`), advances Turbo
   * (TURBO_ADS_REQUIRED ads → 5x grants for 1h) and pays the tiny Pi reward.
   * ATH for Ad Hub ads is credited separately by awardGameATH("watch_and_earn").
   */
  async rewardAd(
    userId: string,
    username: string,
    adId: string
  ): Promise<{ adsToday: number; turboCount: number; turboActivated: boolean }> {
    const REWARD_AMOUNT = 0.000001;
    let outcome = { adsToday: 0, turboCount: 0, turboActivated: false };

    // 1. Verify the ad with Pi Network Service
    const verificationResult = await PiNetworkService.connect(
      false,
      true,
      window.location.hostname
    ).verifyRewardedAd(adId);
    if (verificationResult?.identifier !== adId) {
      throw new Error("Failed to verify the rewarded ad with Pi Network.");
    }

    try {
      await runTransaction(db, async (transaction) => {
        // 2. Check if this ad has already been rewarded
        const rewardedAdRef = doc(db, "piRewardedAds", adId);
        const rewardedAdDoc = await transaction.get(rewardedAdRef);

        if (rewardedAdDoc.exists()) {
          throw new Error(
            "This advertisement reward has already been claimed."
          );
        }

        // 3. Find the user and update their balance
        const userRef = doc(db, "athUsers", userId);
        const userDoc = await transaction.get(userRef);

        if (!userDoc.exists()) {
          throw new Error(`User not found with ID ${userId}.`);
        }

        // 4. Reward the user and record the events
        const data = userDoc.data() || {};
        const today = todayKey();
        const adsToday = (data.adsTodayDate === today ? data.adsTodayCount || 0 : 0) + 1;
        const currentTurboCount = (data.turboChargeCount || 0) + 1;
        const userUpdate: any = {
          picoinBalance: increment(REWARD_AMOUNT),
          adsTodayDate: today,
          adsTodayCount: adsToday,
          updatedAt: serverTimestamp(),
        };

        let turboActivated = false;
        if (currentTurboCount >= TURBO_ADS_REQUIRED) {
          // Activate Turbo Charge (5x for 1 hour) — extends if already active
          const base = data.turboChargeExpiry?.toDate && data.turboChargeExpiry.toDate() > new Date()
            ? data.turboChargeExpiry.toDate()
            : new Date();
          const expiry = new Date(base.getTime() + 60 * 60 * 1000);
          userUpdate.turboChargeCount = 0;
          userUpdate.turboChargeExpiry = Timestamp.fromDate(expiry);
          turboActivated = true;
        } else {
          userUpdate.turboChargeCount = currentTurboCount;
        }
        outcome = {
          adsToday,
          turboCount: turboActivated ? 0 : currentTurboCount,
          turboActivated,
        };

        transaction.update(userRef, userUpdate);

        // Mark the ad as rewarded
        transaction.set(rewardedAdRef, {
          adId: adId,
          userId: userId,
          username: userDoc.data()?.username,
          rewardAmount: REWARD_AMOUNT,
          rewardedAt: serverTimestamp(),
        });

        // Ad reward transaction record removed to reduce Firestore write costs
      });
      return outcome;
    } catch (error) {
      console.error("Error rewarding ad:", error);
      throw new Error(
        (error as Error).message ||
          "An unexpected error occurred while rewarding ad."
      );
    }
  }
  async approvePayment(
    paymentId: string,
    paymentApprovalData: { action: string; userUid: string }
  ): Promise<void> {
    try {
      const userRef = doc(db, "athUsers", paymentApprovalData.userUid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error("User does not exist");
      }

      await runTransaction(db, async (transaction) => {
        // 1. Create payment approval document
        const paymentApprovalRef = doc(db, "piTransactions", paymentId);
        transaction.set(paymentApprovalRef, {
          userUid: paymentApprovalData.userUid,
          action: paymentApprovalData.action,
          completed: false,
          paymentId,
          approvalDate: serverTimestamp(),
        });
      });

      // 2. Approve the payment using PiNetworkService
      await PiNetworkService.connect(
        false,
        true,
        window.location.hostname
      ).approvePiNetworkPayment(paymentId);
    } catch (error) {
      console.error("Error approving payment:", error);
      throw new Error(
        (error as Error).message ||
          "An unexpected error occurred while approving payment."
      );
    }
  }
  async completePayment(
    paymentId: string,
    transactionId: string,
    userUid: string
  ): Promise<{ amount: number; firstDepositFlashBoost: boolean; turbo24h: boolean }> {
    const bonuses = { firstDepositFlashBoost: false, turbo24h: false };
    try {
      // 1. Get payment info from Pi Service to know the amount
      const paymentData = await PiNetworkService.connect(
        false,
        true,
        window.location.hostname
      ).getPiNetworkPaymentInformation(paymentId);
      const amount = paymentData.amount;

      const userRef = doc(db, "athUsers", userUid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error("User does not exist");
      }

      // 2. Complete the payment via Pi Service
      await PiNetworkService.connect(
        false,
        true,
        window.location.hostname
      ).completePiNetworkPayment(paymentId, transactionId);

      // 3. Update Firestore atomically
      await runTransaction(db, async (transaction) => {
        const paymentApprovalRef = doc(db, "piTransactions", paymentId);
        const paymentApprovalDoc = await transaction.get(paymentApprovalRef);

        if (!paymentApprovalDoc.exists()) {
          throw new Error("Payment approval not found");
        }
        if (paymentApprovalDoc.data()?.completed) {
          throw new Error("Payment already completed");
        }

        // Update payment approval document
        transaction.update(paymentApprovalRef, {
          completed: true,
          transactionId,
          completionDate: serverTimestamp(),
          amount,
        });

        // Update user's picoinBalance + deposit tracking (VIP tier + streak)
        const freshUserDoc = await transaction.get(userRef);
        const freshUserData = freshUserDoc.data();
        const today = new Date().toISOString().split("T")[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
        const lastDeposit = freshUserData?.lastDepositDate || "";
        const currentDepositStreak = freshUserData?.depositStreak || 0;

        // Calculate new deposit streak
        let newStreak = 1;
        if (lastDeposit === today) {
          // Already deposited today, keep current streak
          newStreak = currentDepositStreak;
        } else if (lastDeposit === yesterday) {
          // Consecutive day deposit
          newStreak = currentDepositStreak + 1;
        }

        // Deposit bonuses — real, credited here, advertised on the deposit page
        const nowMs = Date.now();
        const later = (ts: any, add: number) => {
          const base = ts?.toDate && ts.toDate().getTime() > nowMs ? ts.toDate().getTime() : nowMs;
          return Timestamp.fromDate(new Date(base + add));
        };
        const depositUpdate: any = {
          picoinBalance: increment(amount),
          totalPiDeposited: increment(amount),
          depositStreak: newStreak,
          lastDepositDate: today,
        };
        const isFirstDeposit = !(freshUserData?.totalPiDeposited > 0);
        if (isFirstDeposit && amount >= FIRST_DEPOSIT_MIN_PI) {
          depositUpdate.flashBoostExpiry = later(freshUserData?.flashBoostExpiry, 24 * 60 * 60 * 1000);
          bonuses.firstDepositFlashBoost = true;
        }
        if (amount >= TURBO_DEPOSIT_MIN_PI) {
          depositUpdate.turboChargeExpiry = later(freshUserData?.turboChargeExpiry, 24 * 60 * 60 * 1000);
          bonuses.turbo24h = true;
        }
        transaction.update(userRef, depositUpdate);

        // Create deposit transaction record
        const depositTransactionRef = doc(db, "athTransactions2", transactionId);
        transaction.set(depositTransactionRef, {
          paymentId,
          id: transactionId,
          amount, // ← Add the amount for tracking
          grantValue: 0,
          type: "deposit",
          crypto: "picoin",
          to: userUid,
          from: "pi_browser_wallet",
          usersInvolvedIds: [userUid, "pi_browser_wallet"],
          combinedUsersId: createCombinedId(userUid, "pi_browser_wallet"),
          revoked: false,
          date: serverTimestamp(),
        });
      });
      return { amount, ...bonuses };
    } catch (error) {
      console.error("Error completing payment:", error);
      throw new Error(
        (error as Error).message ||
          "An unexpected error occurred while completing payment."
      );
    }
  }

  async cancelIncompletePayment(
    paymentId: string,
    piNetworkPaymentDTO: any
  ): Promise<void> {
    try {
      await PiNetworkService.connect(
        false,
        true,
        window.location.hostname
      ).cancelPiNetworkIncompletePayment(paymentId, piNetworkPaymentDTO);
    } catch (error) {
      console.error("Error canceling incomplete payment:", error);
      throw new Error(
        (error as Error).message ||
          "An unexpected error occurred while canceling incomplete payment."
      );
    }
  }

  async cancelPayment(paymentId: string): Promise<void> {
    try {
      await PiNetworkService.connect(
        false,
        true,
        window.location.hostname
      ).cancelPiNetworkPayment(paymentId);
    } catch (error) {
      console.error("Error canceling payment:", error);
      throw new Error(
        (error as Error).message ||
          "An unexpected error occurred while canceling payment."
      );
    }
  }
  async getRecentTransactionPartners(
    userId: string
  ): Promise<{ recentPartners: any[] }> {
    try {
      // 1. Query transactions involving the user, ordered by date.
      const transactionsQuery = query(
        collection(db, "athTransactions2"),
        where("usersInvolvedIds", "array-contains", userId),
        orderBy("date", "desc"),
        limit(50)
      );
      const transactionsSnapshot = await getDocs(transactionsQuery);

      // 2. Process transactions to find up to 10 unique, recent partners.
      const lastTransactionTimes: Record<string, Date> = {};
      const partnerUserIds = new Set<string>();

      for (const doc of transactionsSnapshot.docs) {
        const transactionData = doc.data();
        // Find the other user's ID in the transaction.
        const otherUserId = transactionData.usersInvolvedIds.find(
          (id: string) =>
            id !== userId &&
            id !== "arthereum_app" &&
            id !== "pi_browser_wallet"
        );

        if (otherUserId) {
          const transactionDate = transactionData.date?.toDate();
          // Keep track of the most recent transaction for each partner.
          if (
            !lastTransactionTimes[otherUserId] ||
            transactionDate > lastTransactionTimes[otherUserId]
          ) {
            lastTransactionTimes[otherUserId] = transactionDate;
          }
          partnerUserIds.add(otherUserId);
          // Stop after finding 10 unique partners.
          if (partnerUserIds.size >= 10) {
            break;
          }
        }
      }

      // 3. Fetch user data for the identified partners.
      const recentPartners: any[] = [];
      if (partnerUserIds.size > 0) {
        const usersQuery = query(
          collection(db, "athUsers"),
          where(documentId(), "in", Array.from(partnerUserIds))
        );
        const usersSnapshot = await getDocs(usersQuery);

        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          recentPartners.push({
            id: doc.id,
            username: userData.username,
            profileImage: userData.profileImage,
            lastTransactionTime: lastTransactionTimes[doc.id]?.toISOString(),
          });
        });

        // 4. Sort partners by the most recent transaction time.
        recentPartners.sort(
          (a, b) =>
            new Date(b.lastTransactionTime).getTime() -
            new Date(a.lastTransactionTime).getTime()
        );
      }

      return { recentPartners };
    } catch (error) {
      console.error("Error fetching recent transaction partners:", error);
      // Throw a generic error to be handled by the caller.
      throw new Error("Failed to fetch recent transaction partners.");
    }
  }
  setAuthToken(token: string | null) {
    if (token) {
    } else {
    }
  }

  // Initialize token from storage
  async initialize() {
    const token = await this.getStoredToken();
    if (token) {
      this.setAuthToken(token as string);
    }
  }

  // ===== DAILY REWARDS CONTEST METHODS =====

  /**
   * Get today's date string in YYYY-MM-DD format for contest document IDs
   */
  private getTodayDateString(): string {
    const today = new Date();
    return today.toISOString().split("T")[0]; // e.g., "2025-12-18"
  }

  /**
   * Get previous contest winners (paginated)
   * Fetches from athDailyContests collection where status is "completed"
   */
  async getDailyContestWinners(
    limitCount: number = 10
  ): Promise<{
    winners: Array<{
      id: string;
      date: string;
      username: string;
      profileImage: string | null;
      points: number;
      prizeAmount: number;
      userId: string;
    }>;
  }> {
    try {
      // Query the athDailyContests collection for completed contests with a winner
      const contestsCollection = collection(db, "athDailyContests");
      const winnersQuery = query(
        contestsCollection,
        where("status", "==", "completed"),
        orderBy("completedAt", "desc"),
        limit(limitCount)
      );

      const winnersSnapshot = await getDocs(winnersQuery);
      const winners = winnersSnapshot.docs.map((contestDoc) => {
        const data = contestDoc.data();
        // Map fields from the cloud function's winner data structure
        return {
          id: contestDoc.id, // The date is the document ID (YYYY-MM-DD)
          date: contestDoc.id, // Use document ID as the date
          username: data.winnerUsername || "",
          profileImage: data.winnerProfileImage || null,
          points: data.winnerPoints || 0,
          prizeAmount: data.prizeAmount || 0,
          userId: data.winnerId || "",
        };
      });

      return { winners };
    } catch (error) {
      console.error("Error fetching contest winners:", error);
      throw new Error("Failed to fetch contest winners.");
    }
  }

  /**
   * Get today's contestants sorted by points
   */
  async getDailyContestants(): Promise<{
    contestants: Array<{
      id: string;
      userId: string;
      username: string;
      profileImage: string | null;
      points: number;
      joinedAt: string;
    }>;
    contestDate: string;
  }> {
    try {
      const todayDate = this.getTodayDateString();
      const contestRef = doc(db, "athDailyContests", todayDate);
      const contestantsCollection = collection(contestRef, "contestants");
      const contestantsQuery = query(
        contestantsCollection,
        orderBy("points", "desc"),
        limit(20)
      );

      const contestantsSnapshot = await getDocs(contestantsQuery);
      const contestants = contestantsSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          userId: data.userId,
          username: data.username,
          profileImage: data.profileImage || null,
          points: data.points || 0,
          joinedAt: data.joinedAt?.toDate?.()?.toISOString() || "",
        };
      });

      return { contestants, contestDate: todayDate };
    } catch (error) {
      console.error("Error fetching contestants:", error);
      throw new Error("Failed to fetch contestants.");
    }
  }

  /**
   * Join today's daily contest
   */
  async joinDailyContest(
    userId: string,
    username: string,
    profileImage?: string | null
  ): Promise<{ success: boolean; alreadyJoined: boolean }> {
    try {
      const todayDate = this.getTodayDateString();
      const contestRef = doc(db, "athDailyContests", todayDate);
      const contestantRef = doc(contestRef, "contestants", userId);

      // Check if already joined
      const existingDoc = await getDoc(contestantRef);
      if (existingDoc.exists()) {
        return { success: true, alreadyJoined: true };
      }

      // Join the contest with 1 initial point
      await runTransaction(db, async (transaction) => {
        // Ensure the contest document exists
        const contestDoc = await transaction.get(contestRef);
        if (!contestDoc.exists()) {
          transaction.set(contestRef, {
            date: todayDate,
            createdAt: serverTimestamp(),
            status: "active",
          });
        }

        // Add the contestant
        transaction.set(contestantRef, {
          userId,
          username,
          profileImage: profileImage || null,
          points: 1, // Initial point for joining
          joinedAt: serverTimestamp(),
        });
      });

      return { success: true, alreadyJoined: false };
    } catch (error) {
      console.error("Error joining contest:", error);
      throw new Error("Failed to join contest.");
    }
  }

  /**
   * Award points to a contestant for various actions
   * @param action - 'grant' (+3), 'ad' (+1, max 10/day), 'login' (+2, once/day), 'session' (+5, once/day)
   */
  async awardContestPoints(
    userId: string,
    action: "grant" | "ad_watch"
  ): Promise<{ pointsAwarded: number; totalPoints: number; isContestant: boolean }> {
    const POINTS_MAP: Record<string, number> = {
      grant: 3,
      ad_watch: 3,
    };

    try {
      const todayDate = this.getTodayDateString();

      // Skip Firestore read if we already know user is NOT a contestant today.
      // Saves 1 read per call for non-contestants (majority of users).
      const cacheKey = `not_contestant_${userId}_${todayDate}`;
      if (localStorage.getItem(cacheKey) === "true") {
        return { pointsAwarded: 0, totalPoints: 0, isContestant: false };
      }

      const contestRef = doc(db, "athDailyContests", todayDate);
      const contestantRef = doc(contestRef, "contestants", userId);

      // Check if user is a contestant today
      const contestantDoc = await getDoc(contestantRef);
      if (!contestantDoc.exists()) {
        // Cache the non-contestant result for the rest of the day
        localStorage.setItem(cacheKey, "true");
        return { pointsAwarded: 0, totalPoints: 0, isContestant: false };
      }

      const contestantData = contestantDoc.data();
      const currentPoints = contestantData?.points || 0;

      // Check action-specific limits (None currently needed for grant claims)

      const pointsToAdd = POINTS_MAP[action];
      const newTotalPoints = currentPoints + pointsToAdd;

      // Update based on action type
      const updateData: any = {
        points: increment(pointsToAdd),
        lastPointAt: serverTimestamp(),
      };

      // Action specific mutations

      await runTransaction(db, async (transaction) => {
        transaction.update(contestantRef, updateData);
      });

      return { pointsAwarded: pointsToAdd, totalPoints: newTotalPoints, isContestant: true };
    } catch (error) {
      console.error("Error awarding contest points:", error);
      return { pointsAwarded: 0, totalPoints: 0, isContestant: false };
    }
  }

  /**
   * Daily Login Reward System
   */
  readonly DAILY_REWARDS = [
    { ath: 0.10 },
    { ath: 0.20 },
    { ath: 0.30 },
    { ath: 0.50, extraSpin: 1 },
    { ath: 0.75 },
    { ath: 1.00 },
    { ath: 2.50, mysteryBox: "bronze_box" },
  ];

  async claimDailyReward(userId: string): Promise<UserModelData> {
    try {
      return await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "athUsers", userId);
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const userData = userDoc.data() as User;
        const now = new Date();
        const todayStr = now.toISOString().split("T")[0];
        const lastRewardDate = userData.lastLoginRewardDate
          ? (userData.lastLoginRewardDate as Timestamp).toDate().toISOString().split("T")[0]
          : "";

        if (lastRewardDate === todayStr) {
          throw new Error("Reward already claimed today");
        }

        const streak = userData.currentStreak || 1;
        const dayIndex = (streak - 1) % 7;
        const reward = this.DAILY_REWARDS[dayIndex];

        const updateData: any = {
          arthereumBalance: increment(reward.ath),
          lastLoginRewardDate: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        if (reward.extraSpin) {
          updateData.extraSpins = increment(reward.extraSpin);
        }

        if (reward.mysteryBox) {
          const currentVault = userData.vaultItems || [];
          updateData.vaultItems = [...currentVault, reward.mysteryBox];
        }

        transaction.update(userRef, updateData);

        // Daily login reward transaction record removed to reduce Firestore write costs

        // Award contest points for "login" action if not already done
        // Note: We can't easily call this.awardContestPoints inside a transaction 
        // because it uses another transaction. We'll handle contest logic outside or separately.

        // Refetch and return updated data
        const updatedDoc = await transaction.get(userRef);
        const updatedData = updatedDoc.data() as User;
        
        // Mock mining infos for return
        const miningReferredUserCount = await countRecentMiners(userId);
        const miningInfos = {
          baseGrant: arthereumTokenomics.calculateBaseGrant(),
          nextGrantValue: arthereumTokenomics.calculateNextGrantValue(
            miningReferredUserCount,
            updatedData.stakeBoost
          ),
          miningReferredUserCount: miningReferredUserCount,
          nextGrantDate: updatedData.lastMiningDate
            ? getNextGrantDate((updatedData.lastMiningDate as Timestamp).toDate())
            : new Date(),
        };

        return {
          user: updatedData,
          miningInfos,
        };
      });
    } catch (error) {
      console.error("Error claiming daily reward:", error);
      throw error;
    }
  }

  async awardMysteryBox(userId: string, boxId: string): Promise<void> {
    try {
      const userRef = doc(db, "athUsers", userId);
      await updateDoc(userRef, {
        vaultItems: arrayUnion(boxId),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error awarding mystery box:", error);
      throw error;
    }
  }

  /**
   * Get user's current contest stats
   */
  async getUserContestStats(userId: string): Promise<{
    isContestant: boolean;
    points: number;
    adsWatchedToday: number;
    hasClaimedLoginToday: boolean;
  }> {
    try {
      const todayDate = this.getTodayDateString();
      const contestRef = doc(db, "athDailyContests", todayDate);
      const contestantRef = doc(contestRef, "contestants", userId);

      const contestantDoc = await getDoc(contestantRef);
      if (!contestantDoc.exists()) {
        return {
          isContestant: false,
          points: 0,
          adsWatchedToday: 0,
          hasClaimedLoginToday: false,
        };
      }

      const data = contestantDoc.data();
      return {
        isContestant: true,
        points: data?.points || 0,
        adsWatchedToday: data?.adsWatchedToday || 0,
        hasClaimedLoginToday: data?.hasClaimedLoginToday || false,
      };
    } catch (error) {
      console.error("Error getting contest stats:", error);
      return {
        isContestant: false,
        points: 0,
        adsWatchedToday: 0,
        hasClaimedLoginToday: false,
      };
    }
  }

  /**
   * Check if user is already in today's contest
   */
  async isUserInTodayContest(userId: string): Promise<boolean> {
    try {
      const todayDate = this.getTodayDateString();
      const contestRef = doc(db, "athDailyContests", todayDate);
      const contestantRef = doc(contestRef, "contestants", userId);

      const contestantDoc = await getDoc(contestantRef);
      return contestantDoc.exists();
    } catch (error) {
      console.error("Error checking contest participation:", error);
      return false;
    }
  }

  /**
   * Lucky Spin: rolls the wheel AND credits the prize in one transaction.
   * The prize table is SPIN_REWARDS; the caller only learns the outcome.
   * Cooldown lives on the user document (`lastSpinDate`), not in localStorage.
   * `useExtra` consumes one `extraSpins` instead of the daily free spin.
   */
  async spinWheel(
    userId: string,
    useExtra: boolean = false
  ): Promise<{ index: number; reward: SpinReward; creditedAth: number }> {
    let creditedAth = 0;
    const totalWeight = SPIN_REWARDS.reduce((sum, r) => sum + r.weight, 0);
    const roll = Math.random() * totalWeight;
    let cumulative = 0;
    let index = 0;
    for (let i = 0; i < SPIN_REWARDS.length; i++) {
      cumulative += SPIN_REWARDS[i].weight;
      if (roll < cumulative) {
        index = i;
        break;
      }
    }
    const reward = SPIN_REWARDS[index];

    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "athUsers", userId);
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");
        const data = userDoc.data();

        const updateData: any = {
          updatedAt: serverTimestamp(),
        };

        if (useExtra) {
          if ((data.extraSpins || 0) <= 0) throw new Error("No extra spins left");
          updateData.extraSpins = increment(-1);
        } else {
          const last = data.lastSpinDate?.toDate?.() as Date | undefined;
          if (last && Date.now() - last.getTime() < SPIN_COOLDOWN_MS) {
            throw new Error("Spin cooldown active");
          }
          updateData.lastSpinDate = Timestamp.fromDate(new Date());
        }

        if (reward.type === "points") {
          // ATH from spins is capped per day (SPIN_DAILY_ATH_CAP); spins themselves are not.
          const today = todayKey();
          const spunToday = data.spinRewardsDate === today ? data.todaySpinRewards || 0 : 0;
          creditedAth = parseFloat(Math.max(0, Math.min(reward.value, SPIN_DAILY_ATH_CAP - spunToday)).toFixed(4));
          updateData.arthereumBalance = increment(creditedAth);
          updateData.spinRewardsDate = today;
          updateData.todaySpinRewards = spunToday + creditedAth;
        } else if (reward.type === "picoin") {
          updateData.picoinBalance = increment(reward.value);
        } else if (reward.type === "multiplier") {
          // 1 hour Lucky Tag
          const expiry = new Date();
          expiry.setHours(expiry.getHours() + 1);
          updateData.luckyTagExpiry = Timestamp.fromDate(expiry);
        }

        transaction.update(userRef, updateData);

        // Spin reward transaction record removed to reduce Firestore write costs
      });

      return { index, reward, creditedAth };
    } catch (error) {
      console.error("Error spinning the wheel:", error);
      throw error;
    }
  }

  /** Counts one verified rewarded ad toward the free Bronze box (persisted on the user doc). */
  async recordFreeBoxAd(userId: string): Promise<number> {
    const userRef = doc(db, "athUsers", userId);
    let count = 0;
    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(userRef);
      if (!snap.exists()) throw new Error("User not found");
      count = Math.min(FREE_BOX_ADS_REQUIRED, (snap.data().freeBoxAdsCount || 0) + 1);
      transaction.update(userRef, { freeBoxAdsCount: count, updatedAt: serverTimestamp() });
    });
    return count;
  }

  /** Grants one extra spin after a verified rewarded ad (persisted on the user doc). */
  async grantExtraSpin(userId: string): Promise<void> {
    const userRef = doc(db, "athUsers", userId);
    await updateDoc(userRef, {
      extraSpins: increment(1),
      updatedAt: serverTimestamp(),
    });
  }

  async incrementDailyStat(userId: string, stat: "gamesPlayed" | "sharesCount" | "transfersCount"): Promise<void> {
    try {
      const userRef = doc(db, "athUsers", userId);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) return;

      const userData = userDoc.data() as User;
      const today = new Date().toISOString().split("T")[0];
      const lastUpdate = userData.dailyStats?.lastUpdate || "";

      let dailyStats = userData.dailyStats || { gamesPlayed: 0, sharesCount: 0, transfersCount: 0, lastUpdate: today };

      // Reset if it's a new day
      if (lastUpdate !== today) {
        dailyStats = { gamesPlayed: 0, sharesCount: 0, transfersCount: 0, lastUpdate: today };
      }

      dailyStats[stat] = (dailyStats[stat] || 0) + 1;
      dailyStats.lastUpdate = today;

      await updateDoc(userRef, {
        dailyStats: dailyStats,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error incrementing daily stat:", error);
    }
  }

  /** Home "Flash Boost" card: 1 hour of Flash Boost for FLASH_BOOST_COST Pi. */
  async activateFlashBoost(userId: string) {
    const piCost = FLASH_BOOST_COST;
    try {
      await runTransaction(db, async (transaction) => {
        const userRef = doc(db, "athUsers", userId);
        const userDoc = await transaction.get(userRef);
        if (!userDoc.exists()) throw new Error("User not found");

        const data = userDoc.data() as User;
        if (data.picoinBalance < piCost) throw new Error("Insufficient Pi balance");

        const expiry = new Date();
        expiry.setHours(expiry.getHours() + 1); // 1 hour boost

        transaction.update(userRef, {
          picoinBalance: increment(-piCost),
          flashBoostExpiry: Timestamp.fromDate(expiry),
          updatedAt: serverTimestamp(),
        });

        // Flash boost transaction record removed to reduce Firestore write costs
      });
    } catch (error) {
      console.error("Flash boost failed:", error);
      throw error;
    }
  }

}

const arthereumProvider = new ArthereumProvider();
export default arthereumProvider;
